/**
 * CELESTIAL LAB — chapter 4, and the identity the whole of celestial navigation rests on.
 *
 * FOUNDER's overlay list: "Celestial frame: extend the Earth's axis outward; celestial poles,
 * celestial equator, observer zenith."
 *
 * ── THE ONE IDENTITY ──────────────────────────────────────────────────────────────────────────
 * The altitude of the celestial pole above an observer's horizon EQUALS his latitude.
 *
 * Exactly. Not approximately, not by a table — it falls out of the geometry, and it is why a sextant
 * altitude of Polaris gives latitude almost directly. A cadet is normally told this and believes it
 * on authority. Here he drags the observer from the Equator to the Arctic and watches the pole climb
 * from the horizon to overhead while the number beside it tracks his latitude, because they are the
 * same number.
 *
 * Two more identities come free from the same picture, and they are the reason the celestial sphere
 * is worth drawing at all rather than described:
 *
 *   the DECLINATION of his zenith is his latitude       — latitude is declination overhead
 *   the celestial equator's altitude is 90° − latitude  — which is why it lies low in high latitudes
 *
 * Those three are one fact seen from three sides. Read as three formulae they are three things to
 * memorise and confuse under exam pressure.
 *
 * ── TIME IS THE SAME PICTURE TURNING ──────────────────────────────────────────────────────────
 * The Earth turns 15° of longitude in an hour, so hour angle and longitude are the same measurement
 * in different units. That is stated as arithmetic in every textbook; here the globe turns and the
 * figure moves with it.
 *
 * ── THE EPHEMERIS IS OURS, AND IT IS GATED ────────────────────────────────────────────────────
 * This file first REFUSED to show any ephemeris, reasoning that declination and GHA belong to the
 * Nautical Almanac and a simplified model would hand a candidate a number the Almanac disagrees
 * with. Founder: "we created nautical almanac and tables already." He was right and I had not looked.
 *
 * /api/almanac is live and computed from VSOP87, and nav-tables.mjs gates it against reference data
 * from an INDEPENDENT implementation, holding to the gate's own standard: "a claim of almanac
 * precision must not rest on theory alone." Measured 2026-08-10 — sun worst 0.013', Moon 0.178',
 * star declinations 0.006', SHA 0.022', every planet under 0.02'. Inside almanac tabulation.
 *
 * A REFUSAL IS ONLY HONEST WHILE THE THING REFUSED IS GENUINELY UNAVAILABLE. Once it exists and is
 * validated, the same words become a false statement about our own capability — a worse fault than
 * the one the refusal was guarding against, and harder to notice because it still sounds careful.
 */

import { useState } from 'react';
import NavSphere, { dm } from './navsphere.jsx';
import { Value, useWidgetData } from '../../../packages/ankr-glass/src/react.jsx';
import { plan } from '../../../packages/ankr-vahana/src/plan.mjs';
import { computed, cited } from '../../../packages/ankr-provenance/src/provenance.mjs';
import { API } from './surfaces.jsx';
import { localAlmanac } from './offline/almanac.mjs';

/* With no signal the sky is computed here instead of asked for — same series, same answer. */
const almanacOffline = (vars) => localAlmanac(vars || {});

const W_ALM = { slug: 'almanac-now', layout: 'auto-table',
  wire: { kind: 'rest', route: '/api/almanac', method: 'GET', field: 'almanac' } };

const SPOTS = [
  { lat: 0, lon: 0, label: 'on the Equator' },
  { lat: 23.5, lon: 0, label: 'the Tropic' },
  { lat: 51.5, lon: 0, label: 'the Channel' },
  { lat: 78, lon: 15, label: 'Svalbard' },
];

export default function CelestialLab() {
  const [pos, setPos] = useState({ lat: 51.5, lon: 0 });
  const [show, setShow] = useState({ celestial: true, equator: true, graticule: true, primeMeridian: true });
  const almq = useWidgetData(W_ALM, {}, { baseUrl: API, plan, offline: almanacOffline });
  const alm = almq.data;

  const lat = pos.lat;
  const poleAlt = Math.abs(lat);                 // altitude of the visible celestial pole
  const eqAlt = 90 - Math.abs(lat);              // altitude of the celestial equator at meridian passage
  const hours = (((pos.lon + 540) % 360) - 180) / 15;

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">Celestial lab · the second sphere</div>
      <h2 className="g-h2 lab-h">Your latitude is already written in the sky</h2>
      <p className="g-body">
        Throw the Earth&rsquo;s axis outward and it meets a larger sphere at the
        <b className="lab-key lab-cel"> celestial poles</b>; throw the Equator outward and it becomes
        the <b className="lab-key lab-cel">celestial equator</b>. Straight above you is your
        <b className="lab-key lab-zen"> zenith</b>. Now drag yourself north and watch what happens.
      </p>

      <div className="lab-globe">
        <NavSphere height={340} overlays={show}
          marks={[{ lat: pos.lat, lon: pos.lon, label: 'observer', arcs: false }]}
          onMove={(la, lo) => setPos({ lat: la, lon: lo })} />
      </div>

      <div className="lab-readout lab-sail">
        <div><span className="g-label">Your latitude</span>
             <span className="lab-num">{dm(lat, 'lat')}</span></div>
        <div><span className="g-label">Altitude of the pole</span>
             <span className="lab-num lab-cel">{poleAlt.toFixed(1)}°</span></div>
        <div><span className="g-label">Declination of your zenith</span>
             <span className="lab-num lab-zen">{dm(lat, 'lat')}</span></div>
        <div><span className="g-label">Celestial equator at transit</span>
             <span className="lab-num">{eqAlt.toFixed(1)}°</span></div>
      </div>

      <div className="shell-picker lab-toggles">
        {SPOTS.map((s) => (
          <button key={s.label} className="g-btn" onClick={() => setPos({ lat: s.lat, lon: s.lon })}>
            {s.label}
          </button>
        ))}
      </div>

      <div className="lab-task">
        <div className="g-label">The identity</div>
        <div className="lab-result">
          <Value of={computed(
            `At ${dm(lat, 'lat')} the celestial pole stands ${poleAlt.toFixed(1)}° above your ` +
            `horizon — the same number as your latitude, and the declination of your zenith is the ` +
            `same number again. Take the pole down to the horizon and you are on the Equator; put it ` +
            `overhead and you are at the pole. This is why a sextant altitude of Polaris gives ` +
            `latitude almost directly.`)} />
        </div>
        <p className="g-body lab-why">
          The celestial equator sits {eqAlt.toFixed(1)}° up — always 90° minus your latitude, which is
          why in high latitudes the sun stays low and in the tropics it passes near your zenith. One
          picture, three facts, and they are the same fact.
        </p>
      </div>

      <div className="lab-task">
        <div className="g-label">And time is this picture turning</div>
        <div className="lab-result">
          <Value of={computed(
            `The Earth turns 15° of longitude every hour, so your ${dm(pos.lon, 'lon')} is ` +
            `${Math.abs(hours).toFixed(2)} hours ${hours >= 0 ? 'ahead of' : 'behind'} Greenwich in ` +
            `hour angle. Longitude and hour angle are one measurement in two units — degrees for the ` +
            `chart, hours for the clock.`)} />
        </div>
      </div>

      {/* The frame is geometry; the ephemeris is not, and the difference must not blur. */}
      <div className="lab-task">
        {/* CORRECTED 2026-08-10. This previously REFUSED to work a sight, on the reasoning that
            "the ephemeris belongs to the Almanac". Founder: "we created nautical almanac and tables
            already." He was right and I had not looked.

            /api/almanac is live and computed, and nav-tables.mjs gates it against reference data
            generated by an INDEPENDENT implementation (pyephem), with the gate's own standard:
            "a claim of almanac precision must not rest on theory alone". Measured today — sun worst
            0.013', Moon 0.178', star declinations 0.006', SHA 0.022', the planets all under 0.02'.
            That is inside Nautical Almanac tabulation precision.

            A refusal is only honest while the thing refused is genuinely unavailable. Once it is
            available and validated, the same refusal becomes a false statement about our own
            capability — which is a worse fault than the one it was guarding against. */}
        <Value of={cited(
          `Live from the ship's own almanac: GHA Aries ${alm?.gha_aries_dm || '—'}, ` +
          `Sun GHA ${alm?.sun?.gha_dm || '—'} dec ${alm?.sun?.dec_dm || '—'}, ` +
          `Moon GHA ${alm?.moon?.gha_dm || '—'} dec ${alm?.moon?.dec_dm || '—'}. ` +
          `Computed from VSOP87 and gated against an independent implementation to better than ` +
          `0.2 minutes of arc — inside almanac tabulation.`,
          almq.computedLocally
            ? 'computed ON THIS DEVICE · VSOP87, the same series the server runs — no network involved'
            : '/api/almanac · VSOP87, gated in nav-tables.mjs against pyephem reference data')} />
      </div>

      <div className="lab-def">
        <Value of={cited(
          'The celestial sphere is an imaginary sphere of infinite radius, having the earth as its '
          + 'centre, upon which all celestial bodies appear to be projected; the celestial poles and '
          + 'the celestial equator are the projections of the earth’s poles and equator, and the '
          + 'altitude of the elevated pole is equal to the latitude of the observer.',
          'Bowditch, The American Practical Navigator (1888) — public domain')} />
        <p className="g-body lab-why">
          The last clause of that sentence is the reading on the second line above. You have just
          moved it from something to remember to something you watched happen.
        </p>
      </div>
    </div>
  );
}
